/**
 * Admin-only "subscribe others" panel, rendered inline on the Notifications
 * page below the self-service UI. Lets a platform admin:
 *   - add a managed contact (someone who may never sign in) and vouch for their
 *     phone number,
 *   - pick any recipient (real user or managed contact),
 *   - create / pause / test / delete subscriptions on that recipient's behalf.
 *
 * All calls go through `adminRecipientsApi`, which hits the server's
 * `requireAdmin`-gated `/api/mgmt/recipients/*` endpoints. The self endpoints
 * keep their strict owner-scoping — cross-user writes only happen here.
 */
import { Fragment, useCallback, useEffect, useState } from 'react';
import { adminRecipientsApi, type CreateSubscriptionInput } from '../lib/notifications-api';
import { confirmDialog, toast } from '../lib/dialogs';
import { LoadingBlock } from './Spinner';
import type { NotificationChannel, Recipient, Subscription, SubscriptionCadence } from '../types';

const CHANNELS: { value: NotificationChannel; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'calendarInvite', label: 'Calendar invite' }
];
const CADENCES: SubscriptionCadence[] = ['instant', 'daily', 'weekly'];
const REMINDER_OPTIONS: { hours: number; label: string }[] = [
  { hours: 1, label: '1h' },
  { hours: 6, label: '6h' },
  { hours: 24, label: '1 day' },
  { hours: 168, label: '1 week' }
];

export default function AdminRecipientsSection() {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await adminRecipientsApi.list();
      setRecipients(rows);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load recipients.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  const selected = recipients.find((r) => r.recipientId === selectedId) ?? null;

  return (
    <section aria-labelledby="admin-recipients-heading" className="mb-4">
      <h2 id="admin-recipients-heading" className="h5">
        Subscribe others <span className="badge bg-primary align-middle">Admin</span>
      </h2>
      <p className="small text-muted">
        Create notifications for friends &amp; family. Add a contact with their email (and optionally a
        phone you have permission to text), then subscribe them to events. Only add numbers you have
        permission to message — recipients can reply <code>STOP</code> to opt out at any time.
      </p>

      {error ? <div className="alert alert-danger" role="alert">{error}</div> : null}

      <div className="card mb-3">
        <div className="card-body">
          <h3 className="h6">Add a contact</h3>
          <AddContactForm onAdded={reload} />
        </div>
      </div>

      {loading ? (
        <LoadingBlock label="Loading recipients…" />
      ) : recipients.length === 0 ? (
        <p className="text-muted">No recipients yet. Add a contact above to get started.</p>
      ) : (
        <div className="row g-3">
          <div className="col-md-4">
            <div className="list-group">
              {recipients.map((r) => (
                <button
                  key={r.recipientId}
                  type="button"
                  className={`list-group-item list-group-item-action${r.recipientId === selectedId ? ' active' : ''}`}
                  onClick={() => setSelectedId(r.recipientId)}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <span>
                      <strong>{r.displayName || r.email || r.recipientId}</strong>
                      {r.displayName && r.email ? <span className="d-block small">{r.email}</span> : null}
                    </span>
                    <span className="badge bg-secondary ms-2">{r.activeSubscriptions}</span>
                  </div>
                  <span className="small">
                    {r.kind === 'contact' ? 'Managed contact' : 'User'}
                    {r.phone ? ` · ${r.phone}${r.phoneVerified ? ' ✓' : ''}` : ''}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div className="col-md-8">
            {selected ? (
              <RecipientDetail recipient={selected} onChanged={reload} />
            ) : (
              <p className="text-muted">Select a recipient to manage their subscriptions.</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function AddContactForm({ onAdded }: { onAdded: () => void | Promise<void> }) {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [adminVerifiedPhone, setAdminVerifiedPhone] = useState(true);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      await adminRecipientsApi.createContact({
        email: email.trim(),
        displayName: displayName.trim() || undefined,
        phone: phone.trim() || undefined,
        adminVerifiedPhone: phone.trim() ? adminVerifiedPhone : undefined
      });
      setDisplayName(''); setEmail(''); setPhone('');
      toast.success('Contact added.');
      await onAdded();
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to add contact.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row g-2 align-items-end">
      <div className="col-md-3">
        <label htmlFor="rc-name" className="form-label">Name</label>
        <input id="rc-name" className="form-control" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      </div>
      <div className="col-md-3">
        <label htmlFor="rc-email" className="form-label">Email</label>
        <input id="rc-email" type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="col-md-3">
        <label htmlFor="rc-phone" className="form-label">Phone (E.164, optional)</label>
        <input id="rc-phone" className="form-control" placeholder="+19045551234" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </div>
      <div className="col-md-3 d-flex flex-column gap-2">
        {phone.trim() ? (
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="rc-vouch"
              checked={adminVerifiedPhone}
              onChange={(e) => setAdminVerifiedPhone(e.target.checked)}
            />
            <label htmlFor="rc-vouch" className="form-check-label small">I have permission to text this number</label>
          </div>
        ) : null}
        <button type="button" className="btn btn-primary" onClick={submit} disabled={busy || !email.trim()}>
          {busy ? 'Adding…' : 'Add contact'}
        </button>
      </div>
    </div>
  );
}

function RecipientDetail({ recipient, onChanged }: { recipient: Recipient; onChanged: () => void | Promise<void> }) {
  const [subs, setSubs] = useState<Subscription[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reloadSubs = useCallback(async () => {
    setSubs(null);
    try {
      setSubs(await adminRecipientsApi.listSubs(recipient.recipientId));
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to load subscriptions.');
      setSubs([]);
    }
  }, [recipient.recipientId]);

  useEffect(() => { void reloadSubs(); }, [reloadSubs]);

  async function toggle(s: Subscription) {
    setBusyId(s.id);
    try {
      await adminRecipientsApi.updateSub(recipient.recipientId, s.id, { isActive: !s.isActive });
      await reloadSubs();
      await onChanged();
    } finally { setBusyId(null); }
  }

  async function test(s: Subscription) {
    setBusyId(s.id);
    try {
      await adminRecipientsApi.testSub(recipient.recipientId, s.id);
      toast.success('Test sent to recipient.');
    } catch (e: any) {
      toast.error(e?.message ?? 'Test failed.');
    } finally { setBusyId(null); }
  }

  async function remove(s: Subscription) {
    if (!(await confirmDialog({ title: 'Delete subscription', body: `Delete "${s.name}"?`, confirmLabel: 'Delete', danger: true }))) return;
    setBusyId(s.id);
    try {
      await adminRecipientsApi.removeSub(recipient.recipientId, s.id, true);
      await reloadSubs();
      await onChanged();
    } finally { setBusyId(null); }
  }

  return (
    <div className="card">
      <div className="card-body">
        <h3 className="h6 mb-1">{recipient.displayName || recipient.email || recipient.recipientId}</h3>
        <p className="small text-muted mb-3">
          {recipient.email}
          {recipient.phone ? ` · ${recipient.phone}${recipient.phoneVerified ? ' (verified)' : ' (unverified)'}` : ' · no phone'}
          {recipient.smsOptOut ? ' · SMS opted out' : ''}
        </p>

        <h4 className="h6">New subscription</h4>
        <CreateSubForm recipientId={recipient.recipientId} onCreated={async () => { await reloadSubs(); await onChanged(); }} />

        <hr />

        <h4 className="h6">Subscriptions {subs ? `(${subs.length})` : ''}</h4>
        {!subs ? (
          <LoadingBlock label="Loading subscriptions…" />
        ) : subs.length === 0 ? (
          <p className="small text-muted mb-0">No subscriptions for this recipient yet.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Channels</th>
                  <th scope="col">Cadence</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <Fragment key={s.id}>
                    <tr>
                      <td>{s.name}</td>
                      <td>{s.channels.join(', ')}</td>
                      <td>{s.cadence}</td>
                      <td>{s.isActive ? <span className="badge bg-success">Active</span> : <span className="badge bg-secondary">Paused</span>}</td>
                      <td>
                        <div className="btn-group btn-group-sm" role="group">
                          <button type="button" className="btn btn-outline-secondary" onClick={() => toggle(s)} disabled={busyId === s.id}>
                            {s.isActive ? 'Pause' : 'Resume'}
                          </button>
                          <button type="button" className="btn btn-outline-secondary" onClick={() => test(s)} disabled={busyId === s.id}>Test</button>
                          <button type="button" className="btn btn-outline-danger" onClick={() => remove(s)} disabled={busyId === s.id}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function CreateSubForm({ recipientId, onCreated }: { recipientId: string; onCreated: () => void | Promise<void> }) {
  const [name, setName] = useState('All events');
  const [channels, setChannels] = useState<NotificationChannel[]>(['email']);
  const [cadence, setCadence] = useState<SubscriptionCadence>('daily');
  const [reminders, setReminders] = useState<number[]>([]);
  const [neighborhoods, setNeighborhoods] = useState('');
  const [categories, setCategories] = useState('');
  const [busy, setBusy] = useState(false);

  function toggleChannel(c: NotificationChannel) {
    setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  }
  function toggleReminder(h: number) {
    setReminders((prev) => (prev.includes(h) ? prev.filter((x) => x !== h) : [...prev, h].sort((a, b) => a - b)));
  }

  async function submit() {
    if (channels.length === 0) { toast.error('Pick at least one channel.'); return; }
    setBusy(true);
    try {
      const input: CreateSubscriptionInput = {
        name: name.trim() || 'Subscription',
        channels,
        cadence,
        reminderOffsets: reminders,
        filters: {
          neighborhoods: neighborhoods.split(',').map((s) => s.trim()).filter(Boolean),
          categories: categories.split(',').map((s) => s.trim()).filter(Boolean)
        }
      };
      await adminRecipientsApi.createSub(recipientId, input);
      toast.success('Subscription created.');
      setNeighborhoods(''); setCategories('');
      await onCreated();
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to create subscription.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row g-2 align-items-end mb-2">
      <div className="col-md-4">
        <label htmlFor="cs-name" className="form-label">Name</label>
        <input id="cs-name" className="form-control form-control-sm" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="col-md-4">
        <label className="form-label d-block">Channels</label>
        {CHANNELS.map((c) => (
          <div className="form-check form-check-inline" key={c.value}>
            <input
              type="checkbox"
              className="form-check-input"
              id={`cs-ch-${c.value}`}
              checked={channels.includes(c.value)}
              onChange={() => toggleChannel(c.value)}
            />
            <label className="form-check-label small" htmlFor={`cs-ch-${c.value}`}>{c.label}</label>
          </div>
        ))}
      </div>
      <div className="col-md-4">
        <label htmlFor="cs-cadence" className="form-label">Cadence</label>
        <select id="cs-cadence" className="form-select form-select-sm" value={cadence} onChange={(e) => setCadence(e.target.value as SubscriptionCadence)}>
          {CADENCES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="col-md-6">
        <label className="form-label d-block">Reminders before event</label>
        {REMINDER_OPTIONS.map((r) => (
          <div className="form-check form-check-inline" key={r.hours}>
            <input
              type="checkbox"
              className="form-check-input"
              id={`cs-rem-${r.hours}`}
              checked={reminders.includes(r.hours)}
              onChange={() => toggleReminder(r.hours)}
            />
            <label className="form-check-label small" htmlFor={`cs-rem-${r.hours}`}>{r.label}</label>
          </div>
        ))}
      </div>
      <div className="col-md-3">
        <label htmlFor="cs-hoods" className="form-label">Neighborhoods</label>
        <input id="cs-hoods" className="form-control form-control-sm" placeholder="e.g. Murray Hill" value={neighborhoods} onChange={(e) => setNeighborhoods(e.target.value)} />
      </div>
      <div className="col-md-3">
        <label htmlFor="cs-cats" className="form-label">Categories</label>
        <input id="cs-cats" className="form-control form-control-sm" placeholder="e.g. music, food" value={categories} onChange={(e) => setCategories(e.target.value)} />
      </div>
      <div className="col-12">
        <button type="button" className="btn btn-sm btn-primary" onClick={submit} disabled={busy}>
          {busy ? 'Creating…' : 'Create subscription'}
        </button>
        <span className="form-text ms-2">Leave neighborhoods/categories blank to match all events.</span>
      </div>
    </div>
  );
}
